import { useEffect, useState } from "react";
import Utils from "./Utils";

// This is the primary, go-forward way to do feature flags in IMAS
// agency-web. New flag checks should use isFeatureFlagEnabled/useFeatureFlag
// below. Note this is a THIRD, separate flag mechanism in this codebase --
// do not confuse it with either of the following, and don't extend them:
//   - Utils.isFeatureEnabled(featureName: Features) in Utils.ts: a static,
//     per-environment config baked into the frontend bundle at build time
//     (branches on window.location.host). No runtime targeting, can't be
//     toggled without a redeploy.
//   - views/components/FeatureFlag/: hits BusinessUnitConfig.Service via
//     Ocelot (bearer token required), hardcoded 3-flag-name TS union.
//
// Mirrors the fail-closed, anonymous gateway route used by index.html's
// pre-auth bootstrap check (public/index.html): GET /api/features/{flagKey}
// -> Integrity.Web.Gateway -> Flipt. This route has no auth middleware by
// design, so it must never be used to gate anything sensitive -- only to
// decide whether to show/hide UI.
const FLAG_CHECK_TIMEOUT_MS = 1500;
const CACHE_TTL_MS = 60_000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<boolean>;
}

const cache = new Map<string, CacheEntry>();

const fetchFlag = async (flagKey: string): Promise<boolean> => {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), FLAG_CHECK_TIMEOUT_MS);

  try {
    const response = await fetch(
      `${Utils.getServiceUrlByEnvironment()}/api/features/${flagKey}`,
      {
        headers: { Hostname: Utils.getHost() },
        signal: abortController.signal,
      }
    );
    return response.ok ? await response.json() : false;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Checks a single Flipt feature flag via the anonymous web.gateway proxy.
 * Fails closed (resolves false) on any error or if the check exceeds
 * FLAG_CHECK_TIMEOUT_MS, matching the gateway's and Flipt client's own
 * fail-closed fallback behavior. Results are cached per flagKey for
 * CACHE_TTL_MS, and concurrent callers for the same flagKey share one
 * in-flight request.
 */
export const isFeatureFlagEnabled = (flagKey: string): Promise<boolean> => {
  const cached = cache.get(flagKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise;
  }

  const promise = fetchFlag(flagKey);
  cache.set(flagKey, { promise, expiresAt: Date.now() + CACHE_TTL_MS });
  return promise;
};

/** Clears the in-memory flag cache. Exposed for tests. */
export const clearFeatureFlagCache = (): void => cache.clear();

export interface UseFeatureFlagResult {
  enabled: boolean;
  loading: boolean;
}

/**
 * React hook wrapper around isFeatureFlagEnabled for use in function
 * components. `enabled` defaults to false while loading (fail closed).
 */
export const useFeatureFlag = (flagKey: string): UseFeatureFlagResult => {
  const [state, setState] = useState<UseFeatureFlagResult>({
    enabled: false,
    loading: true,
  });

  useEffect(() => {
    let isMounted = true;
    setState({ enabled: false, loading: true });

    isFeatureFlagEnabled(flagKey).then((enabled) => {
      if (isMounted) {
        setState({ enabled, loading: false });
      }
    });

    return () => {
      isMounted = false;
    };
  }, [flagKey]);

  return state;
};
